import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent } from '../components/ui/card';
import { Car, Loader2, User, Wrench, Truck, AlertCircle, CheckCircle2, Shield, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { getDashboardRoute } from '../config/routes';

// Skills and certifications options
const MECHANIC_SKILLS = [
  "Engine Diagnostics",
  "Brake Systems",
  "Transmission Repair",
  "Electrical Systems",
  "AC/Heating Repair",
  "Oil Change & Maintenance",
  "Suspension & Steering",
  "Exhaust Systems",
  "Battery Replacement",
  "Tire Services",
  "Hybrid/Electric Vehicles",
  "Diesel Engines"
];

const ROADSIDE_SKILLS = [
  "Towing (Flatbed)",
  "Towing (Wheel Lift)",
  "Jump Start Services",
  "Tire Change",
  "Lockout Services",
  "Fuel Delivery",
  "Winch Out/Recovery",
  "Battery Services",
  "Key Replacement",
  "Emergency Response",
  "Heavy Duty Towing",
  "Motorcycle Towing"
];

const ADMIN_SKILLS = [
  "User Management",
  "Provider Approval",
  "Service Management",
  "Analytics & Reporting",
  "Customer Support",
  "Financial Management",
  "Marketing & Promotions",
  "System Configuration"
];

const MECHANIC_CERTIFICATIONS = [
  "ASE Certified",
  "ASE Master Technician",
  "Manufacturer Certified (OEM)",
  "EPA 609 Certified (AC)",
  "State Inspection License",
  "Hybrid/EV Certification",
  "Diesel Technician Certified",
  "L1 Advanced Engine Performance",
  "Brake System Specialist"
];

const ROADSIDE_CERTIFICATIONS = [
  "CDL Class A",
  "CDL Class B",
  "Tow Truck Operator License",
  "TRAA Certified",
  "WreckMaster Certified",
  "First Aid/CPR Certified",
  "Hazmat Endorsement",
  "State DOT Certified",
  "Heavy Recovery Specialist"
];

const ADMIN_CERTIFICATIONS = [
  "Business Administration",
  "Customer Service Certified",
  "Management Training",
  "HR Certification",
  "Financial Management"
];

export default function Register() {
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type');
  const initialType = typeParam === 'mechanic' ? 'mechanic' : typeParam === 'roadside' ? 'roadside_tech' : typeParam === 'admin' ? 'admin' : 'customer';
  
  const [userType, setUserType] = useState(initialType);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedCertifications, setSelectedCertifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setUserType(initialType);
  }, [initialType]);

  // Reset skills/certs when user type changes
  useEffect(() => {
    setSelectedSkills([]);
    setSelectedCertifications([]);
  }, [userType]);

  const isProvider = userType === 'mechanic' || userType === 'roadside_tech';
  const isAdmin = userType === 'admin';
  const requiresApproval = isProvider || isAdmin;
  
  const getAvailableSkills = () => {
    switch (userType) {
      case 'mechanic': return MECHANIC_SKILLS;
      case 'roadside_tech': return ROADSIDE_SKILLS;
      case 'admin': return ADMIN_SKILLS;
      default: return [];
    }
  };
  
  const getAvailableCertifications = () => {
    switch (userType) {
      case 'mechanic': return MECHANIC_CERTIFICATIONS;
      case 'roadside_tech': return ROADSIDE_CERTIFICATIONS;
      case 'admin': return ADMIN_CERTIFICATIONS;
      default: return [];
    }
  };
  
  const availableSkills = getAvailableSkills();
  const availableCertifications = getAvailableCertifications();

  const toggleSkill = (skill) => {
    setSelectedSkills(prev => 
      prev.includes(skill) ? prev.filter(s => s !== skill) : [...prev, skill]
    );
  };

  const toggleCertification = (cert) => {
    setSelectedCertifications(prev => 
      prev.includes(cert) ? prev.filter(c => c !== cert) : [...prev, cert]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (requiresApproval && selectedSkills.length === 0) {
      toast.error('Please select at least one skill');
      return;
    }

    setLoading(true);

    try {
      const userData = {
        name,
        email,
        phone,
        password,
        user_type: userType
      };

      if (requiresApproval) {
        userData.skills = selectedSkills;
        userData.certifications = selectedCertifications;
      }

      const user = await register(userData);
      
      if (user.approval_status === 'pending') {
        toast.success('Application submitted! Please wait for owner approval.');
        navigate('/pending-approval');
      } else {
        toast.success('Account created successfully!');
        navigate(getDashboardRoute(user));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const getTypeTitle = () => {
    switch (userType) {
      case 'mechanic': return 'Join as a Mobile Mechanic';
      case 'roadside_tech': return 'Join as a Roadside Tech';
      case 'admin': return 'Apply for Admin Access';
      default: return 'Get Help Fast';
    }
  };

  const getTypeSubtitle = () => {
    switch (userType) {
      case 'mechanic': return 'Earn money providing mobile repair services.';
      case 'roadside_tech': return 'Help stranded drivers get back on the road.';
      case 'admin': return 'Manage users, services, and platform operations.';
      default: return 'Connect with certified mechanics instantly.';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="register-page">
      {/* Left Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.pexels.com/photos/6870309/pexels-photo-6870309.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')` 
          }}
        />
        <div className="absolute inset-0 bg-gray-900/60" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white">
            <h2 className="text-4xl font-bold mb-4 font-['Chivo']">{getTypeTitle()}</h2>
            <p className="text-xl text-gray-200">{getTypeSubtitle()}</p>
            {requiresApproval && (
              <div className="mt-6 p-4 bg-yellow-500/20 rounded-lg border border-yellow-400/30">
                <AlertCircle className="w-6 h-6 text-yellow-300 mx-auto mb-2" />
                <p className="text-yellow-100 text-sm">
                  {isAdmin ? 'Admin accounts require owner approval' : 'Provider accounts require admin approval'} before activation
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-lg">
          <Link to="/" className="flex items-center gap-2 mb-6" data-testid="register-logo">
            <div className="w-10 h-10 bg-blue-600 rounded-sm flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900 font-['Chivo']">Jiffys Mobile</span>
          </Link>

          <h1 className="text-3xl font-bold text-gray-900 mb-2 font-['Chivo']">Create your account</h1>
          <p className="text-gray-600 mb-6">Sign up to get started</p>

          {/* User Type Toggle - 3 options */}
          <div className="flex gap-1 mb-6 p-1 bg-gray-100 rounded-lg">
            <button
              type="button"
              onClick={() => setUserType('customer')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-md font-medium transition-all text-sm ${
                userType === 'customer' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="register-type-customer"
            >
              <User className="w-4 h-4" />
              Customer
            </button>
            <button
              type="button"
              onClick={() => setUserType('mechanic')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-md font-medium transition-all text-sm ${
                userType === 'mechanic' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="register-type-mechanic"
            >
              <Wrench className="w-4 h-4" />
              Mechanic
            </button>
            <button
              type="button"
              onClick={() => setUserType('roadside_tech')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-md font-medium transition-all text-sm ${
                userType === 'roadside_tech' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="register-type-roadside"
            >
              <Truck className="w-4 h-4" />
              Roadside
            </button>
            <button
              type="button"
              onClick={() => setUserType('admin')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-md font-medium transition-all text-sm ${
                userType === 'admin' 
                  ? 'bg-white text-purple-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="register-type-admin"
            >
              <Shield className="w-4 h-4" />
              Admin
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <Label htmlFor="name" className="text-gray-700 font-medium">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="mt-1 h-11 rounded-sm border-gray-300 focus:ring-2 focus:ring-blue-600"
                  required
                  data-testid="register-name"
                />
              </div>

              <div className="col-span-2 sm:col-span-1">
                <Label htmlFor="phone" className="text-gray-700 font-medium">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="mt-1 h-11 rounded-sm border-gray-300 focus:ring-2 focus:ring-blue-600"
                  required={requiresApproval}
                  data-testid="register-phone"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-gray-700 font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1 h-11 rounded-sm border-gray-300 focus:ring-2 focus:ring-blue-600"
                required
                data-testid="register-email"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="password" className="text-gray-700 font-medium">Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min 6 chars"
                    className="h-11 rounded-sm border-gray-300 focus:ring-2 focus:ring-blue-600 pr-10"
                    required
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-gray-700 font-medium">Confirm</Label>
                <div className="relative mt-1">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm"
                    className="h-11 rounded-sm border-gray-300 focus:ring-2 focus:ring-blue-600 pr-10"
                    required
                    data-testid="register-confirm-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    data-testid="toggle-confirm-password"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Skills Selection for Providers and Admins */}
            {requiresApproval && (
              <Card className={`${isAdmin ? 'border-purple-200 bg-purple-50/50' : 'border-blue-200 bg-blue-50/50'}`}>
                <CardContent className="pt-4">
                  <Label className="text-gray-700 font-medium flex items-center gap-2 mb-3">
                    {isAdmin ? <Shield className="w-4 h-4 text-purple-600" /> : <Wrench className="w-4 h-4 text-blue-600" />}
                    {isAdmin ? 'Admin Capabilities' : 'Skills & Expertise'} <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-xs text-gray-500 mb-3">Select all that apply</p>
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                    {availableSkills.map((skill) => (
                      <label 
                        key={skill} 
                        className={`flex items-center gap-2 p-2 rounded-md cursor-pointer text-sm transition-colors ${
                          selectedSkills.includes(skill) 
                            ? `${isAdmin ? 'bg-purple-100 border border-purple-300' : 'bg-blue-100 border border-blue-300'}` 
                            : 'bg-white border border-gray-200 hover:border-blue-200'
                        }`}
                      >
                        <Checkbox
                          checked={selectedSkills.includes(skill)}
                          onCheckedChange={() => toggleSkill(skill)}
                        />
                        <span className={selectedSkills.includes(skill) ? `${isAdmin ? 'text-purple-700' : 'text-blue-700'} font-medium` : 'text-gray-700'}>
                          {skill}
                        </span>
                      </label>
                    ))}
                  </div>
                  {selectedSkills.length > 0 && (
                    <p className={`text-xs ${isAdmin ? 'text-purple-600' : 'text-blue-600'} mt-2 flex items-center gap-1`}>
                      <CheckCircle2 className="w-3 h-3" />
                      {selectedSkills.length} {isAdmin ? 'capabilit' : 'skill'}{selectedSkills.length !== 1 ? 'ies' : 'y'} selected
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Certifications Selection for Providers and Admins */}
            {requiresApproval && (
              <Card className="border-green-200 bg-green-50/50">
                <CardContent className="pt-4">
                  <Label className="text-gray-700 font-medium flex items-center gap-2 mb-3">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    Certifications (Optional)
                  </Label>
                  <p className="text-xs text-gray-500 mb-3">Certifications help build trust with customers</p>
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                    {availableCertifications.map((cert) => (
                      <label 
                        key={cert} 
                        className={`flex items-center gap-2 p-2 rounded-md cursor-pointer text-sm transition-colors ${
                          selectedCertifications.includes(cert) 
                            ? 'bg-green-100 border border-green-300' 
                            : 'bg-white border border-gray-200 hover:border-green-200'
                        }`}
                      >
                        <Checkbox
                          checked={selectedCertifications.includes(cert)}
                          onCheckedChange={() => toggleCertification(cert)}
                        />
                        <span className={selectedCertifications.includes(cert) ? 'text-green-700 font-medium' : 'text-gray-700'}>
                          {cert}
                        </span>
                      </label>
                    ))}
                  </div>
                  {selectedCertifications.length > 0 && (
                    <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      {selectedCertifications.length} certification{selectedCertifications.length !== 1 ? 's' : ''} selected
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Admin Approval Notice for Providers and Admins */}
            {requiresApproval && (
              <div className={`flex items-start gap-3 p-3 ${isAdmin ? 'bg-purple-50 border-purple-200' : 'bg-amber-50 border-amber-200'} border rounded-lg`}>
                <AlertCircle className={`w-5 h-5 ${isAdmin ? 'text-purple-600' : 'text-amber-600'} flex-shrink-0 mt-0.5`} />
                <div>
                  <p className={`font-medium ${isAdmin ? 'text-purple-800' : 'text-amber-800'} text-sm`}>
                    {isAdmin ? 'Owner Approval Required' : 'Admin Approval Required'}
                  </p>
                  <p className={`text-xs ${isAdmin ? 'text-purple-700' : 'text-amber-700'} mt-1`}>
                    {isAdmin 
                      ? 'Admin accounts require owner approval. Only the platform owner can approve admin registrations.'
                      : `All ${userType === 'mechanic' ? 'mechanic' : 'roadside technician'} accounts require admin approval before you can accept jobs. You'll receive an email once approved.`
                    }
                  </p>
                </div>
              </div>
            )}

            <Button 
              type="submit" 
              className={`w-full h-12 ${isAdmin ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'} text-white font-semibold rounded-sm`}
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : requiresApproval ? (
                'Submit Application'
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          {!requiresApproval && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-gray-50 text-gray-500">or continue with</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full h-12 border-gray-300 text-gray-700 font-medium rounded-sm hover:bg-gray-50"
                onClick={loginWithGoogle}
                data-testid="register-google"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>
            </>
          )}

          <p className="mt-6 text-center text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 font-semibold hover:underline" data-testid="register-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
